/*
  ═════════════════════════════════════════════════════════════════════════════
  WHAT HAPPENS WHEN THE ACCESS TOKEN RUNS OUT

  What was wrong
  ──────────────
  Nothing handled a 401. Not the fetch client, not the RTK Query base query. An
  access token has a short life, so a tab left open through a lesson — or a gate
  screen sitting idle between the morning drop-off and three o'clock, which is
  the normal way this product is used — came back to a page that looked signed
  in and did nothing at all. Every request failed, the sidebar still showed the
  user's name, the dismissal board quietly stopped updating, and the only way
  out was for somebody to work out that clearing site data was the answer.

  Worse than an error, because it did not look like one.

  What happens now
  ────────────────
  A 401 on any ordinary request means one of two things, and they are told apart
  rather than guessed at:

    the session can be renewed  → the refresh cookie is still good. One refresh,
                                  and the original request is retried once. The
                                  person notices nothing, which is the point.
    the session is over         → sign them out properly: drop the token, clear
                                  the store, and let the route guards put them
                                  on the right login screen, with a line saying
                                  why they are there.

  Single-flight, deliberately
  ───────────────────────────
  A dashboard fires six queries at once and they all fail together. Six parallel
  refreshes would race, and five of them would present a refresh token the first
  one had already rotated — which, on an API that rotates them, invalidates the
  session the refresh was meant to save. So every caller waits on one promise.

  Auth routes are exempt
  ──────────────────────
  A 401 from a login attempt means "wrong password", not "your session ended".
  Treating those alike would sign a person out for mistyping, and loop forever
  on the refresh call itself.
  ═════════════════════════════════════════════════════════════════════════════
*/

export const SESSION_EXPIRED_KEY = 'safepass:session-expired';

type Listener = () => void;
const listeners = new Set<Listener>();

/** Called when the session is over and the app should return to a login screen. */
export function onSessionEnded(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** `/auth/login`, `/auth/refresh`, `/auth/otp/...` — see the note above. */
export function isAuthPath(url: string): boolean {
  return /\/auth\//.test(url);
}

let inFlight: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        /* The refresh token is an httpOnly cookie — it is never readable here,
           which is the whole reason it is the thing that outlives the tab. */
        credentials: 'include',
      });
      if (!res.ok) return null;

      const body: unknown = await res.json().catch(() => null);
      const token = (body as { data?: { accessToken?: string } } | null)?.data?.accessToken;
      if (typeof token !== 'string' || !token) return null;

      localStorage.setItem('access_token', token);
      return token;
    } catch {
      /* Offline is not an expired session. Returning null ends the session,
         which is the safe direction: the alternative is a screen that looks
         signed in and is not. */
      return null;
    } finally {
      /* Cleared here rather than in a .finally() on the caller, so the next
         expiry — an hour later — starts a fresh refresh rather than reusing
         this one's resolved promise. */
      inFlight = null;
    }
  })();

  return inFlight;
}

/*
  End it. Local only — no call to /auth/logout, because the token that would
  authorise that call is the one that just expired.
*/
export function endSession(): void {
  localStorage.removeItem('access_token');
  takenAnswer = null;
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
  } catch {
    /* Private mode. The sign-out still happens; only the explanation is lost. */
  }
  listeners.forEach((fn) => fn());
}

/*
  Read once, by the login screen, so the notice does not outlive its moment.

  The answer is remembered rather than re-read, because this is called from a
  `useState` initializer and React's StrictMode runs those TWICE in development.
  Read-and-delete twice means the first call takes the flag and the second — the
  one whose return value React actually keeps — finds nothing, so the notice
  never appears. It cost a browser test to find, and it would have been
  invisible in production, where StrictMode does not double-invoke: the bug only
  shows up in the environment where you are looking for it least.

  `endSession` clears this memo, so a second expiry in the same tab is announced
  again.
*/
let takenAnswer: boolean | null = null;

export function takeSessionExpiredFlag(): boolean {
  if (takenAnswer !== null) return takenAnswer;
  try {
    const had = sessionStorage.getItem(SESSION_EXPIRED_KEY) !== null;
    if (had) sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    takenAnswer = had;
    return had;
  } catch {
    takenAnswer = false;
    return false;
  }
}
